/**
 * Anlage 8 des Kleinspeicher-Kaufvertrags: der Pachtvertrag Kleinspeicher.
 *
 * Der Kaufvertrag fuehrt den Pachtvertrag in seinem Anlagenverzeichnis als
 * "Anlage 8 - Pachtvertrag Kleinspeicher als Alternative zur Kaufabwicklung"
 * und verweist im Text auf den "als Anlage 8 beigefuegten Pachtvertrag". Damit
 * dieser Verweis nicht ins Leere zeigt, wird der Pachtvertrag aus derselben
 * Vorlage gerendert wie der eigenstaendige Pachtvertrag und an die Kaufvertrags-
 * PDF angehaengt -- eine gleich aussehende Kopie, kein zweites Layout.
 *
 * Diese Datei haelt fest, was Massenversand und Einzelvertrag dafuer gemeinsam
 * brauchen. Vorher lag die Logik ausschliesslich im Massenversand.
 */

export const KLEINSPEICHER_PURCHASE_CONTRACT_KEY = 'kaufvertrag-kleinspeicher';
export const KLEINSPEICHER_LEASE_CONTRACT_KEY_JZ = 'pachtvertrag-kleinspeicher-jz';
export const KLEINSPEICHER_LEASE_CONTRACT_KEY_EZ = 'pachtvertrag-kleinspeicher-ez';

/** Nummer laut Anlagenverzeichnis des Kaufvertrags. */
export const KLEINSPEICHER_LEASE_ANNEX_NUMBER = 8;

/** Erkennungsmerkmal der Anlage in der Warteschlange. */
export const KLEINSPEICHER_LEASE_ANNEX_KEY = 'kleinspeicher-lease-contract';

export const LEASE_NUMBER_INITIAL_VALUES = {
  annualPerKw: '',
  oneTimePerKw: '',
  extensionSharePercent: '',
};

/**
 * Die Pachtzahlen kommen aus keinem Bitrix-Feld, sondern werden von Hand
 * eingetragen. Einheiten und Tausenderpunkte fliegen raus, damit die Vorlage
 * ihre eigene Formatierung setzen kann.
 */
export function normalizeLeaseNumberInput(value) {
  return String(value || '')
    .replace(/€/g, '')
    .replace(/%/g, '')
    .replace(/\/\s*kW/ig, '')
    .replace(/\bnetto\b/ig, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Dieselbe Zahl unter mehreren Schreibweisen ablegen: die Vorlagen kennen den
 * Platzhalter teils mit Umlaut, teils in der ae-Schreibweise.
 */
export function assignMappingAliases(target, aliases, value) {
  if (!value) return;
  aliases.forEach((alias) => {
    target[alias] = value;
  });
}

/**
 * Spaetere Quellen gewinnen -- aber nur, wenn sie tatsaechlich etwas enthalten.
 * Ein leeres Feld aus der Pachtvorlage soll einen gefuellten Wert aus dem
 * Kaufvertrag nicht ueberschreiben.
 */
export function mergeMappingFieldsPreferFilled(...sources) {
  const merged = {};
  sources.forEach((source) => {
    if (!source || typeof source !== 'object' || Array.isArray(source)) return;
    Object.entries(source).forEach(([key, value]) => {
      const fieldKey = String(key || '').trim();
      if (!fieldKey) return;
      const valueText = String(value == null ? '' : value).trim();
      if (valueText || !Object.prototype.hasOwnProperty.call(merged, fieldKey)) {
        merged[fieldKey] = value == null ? '' : String(value);
      }
    });
  });
  return merged;
}

/**
 * Welche Zahlen die Anlage braucht, haengt an der Zahlweise: der
 * Jahreszahlungs-Vertrag fragt nach der jaehrlichen Pacht, der
 * Einmalzahlungs-Vertrag nach der einmaligen. Der Verlaengerungsanteil steht in
 * beiden.
 */
export function getLeaseNumberRequirements(leaseContractKeys = []) {
  const keys = new Set(
    (Array.isArray(leaseContractKeys) ? leaseContractKeys : [leaseContractKeys])
      .map((key) => String(key || '').trim())
      .filter(Boolean)
  );

  return {
    leaseContractKeys: Array.from(keys),
    requiresAnnual: keys.has(KLEINSPEICHER_LEASE_CONTRACT_KEY_JZ),
    requiresOneTime: keys.has(KLEINSPEICHER_LEASE_CONTRACT_KEY_EZ),
    requiresExtensionShare: keys.size > 0,
  };
}

export function buildLeaseNumberMappingFields(values, requirements) {
  const fields = {};
  const annual = normalizeLeaseNumberInput(values?.annualPerKw);
  const oneTime = normalizeLeaseNumberInput(values?.oneTimePerKw);
  const extensionShare = normalizeLeaseNumberInput(values?.extensionSharePercent);

  if (requirements?.requiresAnnual && annual) {
    assignMappingAliases(fields, ['Pacht_jährlich_pro_kW', 'Pacht_jaehrlich_pro_kW'], annual);
  }
  if (requirements?.requiresOneTime && oneTime) {
    assignMappingAliases(fields, ['Pacht_einmalig_pro_kW'], oneTime);
  }
  if (requirements?.requiresExtensionShare && extensionShare) {
    assignMappingAliases(fields, ['% in Verlängerung', '% in Verlaengerung'], extensionShare);
  }

  return fields;
}

export function getMissingLeaseNumberLabels(values, requirements) {
  const missing = [];
  if (requirements?.requiresAnnual && !normalizeLeaseNumberInput(values?.annualPerKw)) {
    missing.push('Pacht jährlich pro kW');
  }
  if (requirements?.requiresOneTime && !normalizeLeaseNumberInput(values?.oneTimePerKw)) {
    missing.push('Pacht einmalig pro kW');
  }
  if (requirements?.requiresExtensionShare && !normalizeLeaseNumberInput(values?.extensionSharePercent)) {
    missing.push('% in Verlängerung');
  }
  return missing;
}
